import {deleteField} from '@angular/fire/firestore';
import {DisableRuleSupplierActionEnum} from './disable-rule-supplier-action.enum';
import {IDisableStockItemRule, IOnHandDisableRule} from './stock-item-rule.interface';

type IFirestoreOnHandDisableRule = Omit<IOnHandDisableRule, 'regularSuppCode' | 'supplierAction'> & {
  supplierAction?: DisableRuleSupplierActionEnum | ReturnType<typeof deleteField>;
  regularSuppCode?: string | ReturnType<typeof deleteField>;
};

type IFirestoreDisableStockItemRule = {
  onHandNotZero: IFirestoreOnHandDisableRule;
  onHandZero: IFirestoreOnHandDisableRule;
};

/**
 * Builds a Firestore-safe on-hand disable rule. Omits supplier fields when action is "none"
 * and uses deleteField() to clear previously saved supplier settings on merge writes.
 */
function buildOnHandDisableRuleForFirestore(rule: IOnHandDisableRule | undefined): IFirestoreOnHandDisableRule {
  const supplierAction: DisableRuleSupplierActionEnum = rule?.supplierAction ?? DisableRuleSupplierActionEnum.none;

  const firestoreRule: IFirestoreOnHandDisableRule = {
    descPrefix: rule?.descPrefix?.trim() ?? '',
    descSuffix: rule?.descSuffix?.trim() ?? '',
    lineColour: rule?.lineColour ?? 0,
    onHoldCode: rule?.onHoldCode ?? 0,
  };

  if (supplierAction === DisableRuleSupplierActionEnum.set) {
    firestoreRule.supplierAction = DisableRuleSupplierActionEnum.set;
    firestoreRule.regularSuppCode = rule?.regularSuppCode?.trim() ?? '';
    return firestoreRule;
  }

  if (supplierAction === DisableRuleSupplierActionEnum.remove) {
    firestoreRule.supplierAction = DisableRuleSupplierActionEnum.remove;
    firestoreRule.regularSuppCode = deleteField();
    return firestoreRule;
  }

  firestoreRule.supplierAction = deleteField();
  firestoreRule.regularSuppCode = deleteField();
  return firestoreRule;
}

/**
 * Sanitizes disable stock item rules before Firestore merge writes.
 */
export function sanitizeDisableStockItemRuleForFirestore(settings: IDisableStockItemRule): IFirestoreDisableStockItemRule {
  return {
    onHandNotZero: buildOnHandDisableRuleForFirestore(settings.onHandNotZero),
    onHandZero: buildOnHandDisableRuleForFirestore(settings.onHandZero),
  };
}

/**
 * Normalizes disable stock item rules loaded from Firestore. Missing supplier fields mean no supplier change.
 */
export function normalizeDisableStockItemRuleFromFirestore(settings: IDisableStockItemRule | undefined): IDisableStockItemRule {
  const normalizeRule = (rule: IOnHandDisableRule | undefined): IOnHandDisableRule => ({
    descPrefix: rule?.descPrefix ?? '',
    descSuffix: rule?.descSuffix ?? '',
    lineColour: rule?.lineColour ?? 0,
    onHoldCode: rule?.onHoldCode ?? 0,
    supplierAction: rule?.supplierAction ?? DisableRuleSupplierActionEnum.none,
    regularSuppCode: rule?.regularSuppCode ?? '',
  });

  return {
    onHandNotZero: normalizeRule(settings?.onHandNotZero),
    onHandZero: normalizeRule(settings?.onHandZero),
  };
}
